import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import {
  billingPaymentReseted,
  getStripeKey,
  stripeClientSecretReset,
  stripeUpdatePaymentMethodError,
  updatePaymentMethod,
} from '../../../actions';
import { PAYMENT_METHOD_IDS, POLICY_STATUS } from '../../../constants';
import { PaymentMethod } from './PaymentMethod';
import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';

import bankIcon from '../../../assets/billingPayments/saving-bank.svg';
import checkIcon from '../../../assets/billingPayments/default-check.svg';
import creditCardIcon from '../../../assets/billingPayments/credit-card.svg';
import addIconBlue from '../../../assets/myPets/add-icon-blue.svg';
import loadingWheel from '../../../assets/lottieFiles/loading-blue.json';

import './css/PaymentMethodCard.css';
import { useMixpanel, useWebViewReskin } from '../../../hooks';
import { StripeForm } from '../../common/stripe/StripeForm';
import FigoLottie from '../../common/FigoLottie';
import { useGetRecaptchaToken } from '../../../hooks/useRecaptchaValidator';
import { EV_RECAPTCHA_ERROR } from '../../../services/mixpanel';
import { StripeErrorModal } from './StripeErrorModal';

/**
 * This card component is used in more menu stack.
 */
const PaymentMethodCard = ({
  diamondPolicyId,
  policyInformation,
  policyNumber,
}) => {
  const { t } = useTranslation('myAccount');
  const i18nPayments = useTranslation('paymentMethod');
  const dispatch = useDispatch();
  const useMobileReskin = useWebViewReskin();
  const store = useSelector(({
    accountLogin,
    billingPayments,
    personalInformation,
    policies,
  }) => ({
    accountLogin,
    billingPayments,
    personalInformation,
    policies,
  }));
  const [showStripe, setShowStripe] = useState(false);
  const [error, setError] = useState('');
  const getRecaptchaToken = useGetRecaptchaToken();
  const { sendEvent } = useMixpanel();

  const {
    accountLogin: { userInfo },
    personalInformation,
    billingPayments: {
      billingDetails,
      loadingBillingInfo,
      stripeClientSecret,
      stripeError,
      stripeKey,
      stripeKeyError,
      stripeKeyLoading,
      updatingPaymentMethod,
    },
  } = store;

  useEffect(() => {
    if (stripeKeyError) {
      sendEvent(EV_RECAPTCHA_ERROR(stripeKeyError));
    }
  }, [stripeKeyError, sendEvent]);

  const billingDetailsValidation =
    billingDetails?.ACHAccountNumber !== null
    || billingDetails?.CCCardNumber !== null;

  const userInformation = {
    address1: personalInformation.address1,
    address2: personalInformation.address2,
    city: personalInformation.city,
    state: personalInformation.state,
    zipCode: personalInformation.zipCode,
  };

  const closeStripeForm = () => setShowStripe(false);

  const onUpdatePaymentMethod = ({
    paymentMethodData,
    stripePaymentData,
  }) => {
    const { PolicyNumber } = policyInformation || {};

    dispatch(stripeClientSecretReset());
    setShowStripe(false);

    dispatch(updatePaymentMethod({
      paymentMethodData,
      policies: [],
      PolicyNumber,
      sameBillingAddress: true,
      stripePaymentData,
      userInformation,
    }));
  };

  const onErrorPaymentMethod = () => {
    setError(i18nPayments.t('stripe.errorMessage'));
  };

  const onAddPaymentMethod = async () => {
    setShowStripe(true);

    try {
      const token = await getRecaptchaToken();

      if (token) {
        const policy = diamondPolicyId;
        dispatch(getStripeKey({ policy, token }));
      } else {
        setShowStripe(false);
        setError(i18nPayments.t('stripe.errorMessage'));
      }
    } catch (_) {
      setError(i18nPayments.t('stripe.errorMessage'));
    }
  };

  function renderUpdateButton() {
    const hideEditButton = showStripe;

    if (stripeKeyLoading) {
      return (
        <div className="Payment-method-card-tooltip-button">
          <FigoLottie
            animationData={loadingWheel}
            height={30}
            width={30}
          />
        </div>
      );
    }

    if (hideEditButton) {
      return null;
    }

    return (
      <button
        className="Payment-method-card-tooltip-button"
        disabled={showStripe}
        onClick={onAddPaymentMethod}
        type="button"
      >
        <div className="No-click">
          <span className="Payment-method-card-button-details">
            {t('billingAndPayments.edit')}
          </span>
        </div>
      </button>
    );
  }

  const isCreditCard = billingDetails?.PayMethod
    === PAYMENT_METHOD_IDS.creditCard;
  const isBankAccount = billingDetails?.PayMethod === PAYMENT_METHOD_IDS.ach;
  const validPolicies =
    policyInformation?.Status === POLICY_STATUS.active
    || policyInformation?.Status === POLICY_STATUS.future;
  const stripeValidations = !showStripe
    && !billingDetailsValidation
    && validPolicies;

  const onCloseStripeModalError = () => {
    setError('');
    dispatch(stripeUpdatePaymentMethodError(''));
    setShowStripe(false);
  };

  useEffect(() => {
    dispatch(billingPaymentReseted());
  }, []);

  if (updatingPaymentMethod || loadingBillingInfo) {
    return (
      <FigoLoadingOverlay visible={updatingPaymentMethod} />
    );
  }

  return (
    <>
      {billingDetailsValidation && billingDetails && (
        <div
          className="Payment-method-card-container"
          data-default={useMobileReskin}
        >
          {useMobileReskin && (
            <div className="Payment-method-card-default-label">
              <img alt="" src={checkIcon} />

              {t('billingAndPayments.default')}
            </div>
          )}

          <PaymentMethod
            billingDetails={billingDetails}
            icon={isBankAccount ? bankIcon : creditCardIcon}
            isCreditCard={isCreditCard}
            title={isBankAccount ? t('billingAndPayments.bankAccountEnding')
              : t('billingAndPayments.creditCardEnding')}
          />

          <div>
            {renderUpdateButton()}
          </div>
        </div>
      )}

      {showStripe && stripeClientSecret && stripeKey && (
        <StripeForm
          clientSecret={stripeClientSecret}
          email={userInfo?.LoginEmail || ''}
          name={`${userInfo?.FirstName || ''} ${userInfo?.LastName || ''}`}
          onClose={closeStripeForm}
          onConfirm={onUpdatePaymentMethod}
          onError={onErrorPaymentMethod}
          selectedPolicy={policyInformation?.PolicyNumber
            || policyNumber}
          stripeKey={stripeKey}
        />
      )}

      <StripeErrorModal
        onClose={onCloseStripeModalError}
        show={stripeError || stripeKeyError || error}
      />

      {stripeValidations && (
        <button
          className="Payment-method-button-add-method"
          onClick={onAddPaymentMethod}
          type="button"
        >
          <p>
            <img
              alt=""
              className="Billing-info-screen-button-image"
              src={addIconBlue}
            />
            {t('billingDetailsCard.addPaymentMethod')}
          </p>
        </button>
      )}
    </>
  );
};

export default PaymentMethodCard;
